import React, { useState, useEffect } from 'react';
import Button from '@atlaskit/button';
import { DynamicResultTable, FormattedDate, SimpleTD, useSidebar, OpenSidebarPayload } from '@css-xp/base';
import { NOT_AVAILABLE } from '@atlassian/cssxp-utils-entitlement';
import { getTransactionAccountBalance } from '@atlassian/temp-proxies';
import { GloballyAllowedSidebar } from '@atlassian/cssxp-utils-generic';
import defaultTo from 'lodash/defaultTo';
import capitalize from 'lodash/capitalize';

export interface TransactionAccountBalanceProps {
  transactionAccountId: string;
  label: string;
}

export const emptyStateOrderProp = {
  header: '',
  description: 'No account credits or debits found',
  imageUrl: '',
  testId: 'no-results-account-balance',
};

const descriptiopMapping: Record<string, string> = {
  credit_note: 'Credit - SLA Credit',
  invoice_too_small: 'Credit - Negative invoice',
  applied_to_invoice: 'Debit - Applied to invoice',
};

const TransactionAccountBalanceTable = ({ transactionAccountId, label }: TransactionAccountBalanceProps) => {
  const { openSidebar } = useSidebar();
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [accountBalanceData, setAccountBalanceData] = useState([]);

  useEffect(() => {
    async function fetchTransactionAccountBalance() {
      const response = await getTransactionAccountBalance(transactionAccountId);

      if (response?.data || response === null) {
        setIsLoading(false);
      }

      if (response?.data) {
        setAccountBalanceData(response?.data);
      }
    }
    fetchTransactionAccountBalance();
  }, []);

  const handleSidebarAction = (openSidebarPayload: OpenSidebarPayload) => {
    openSidebar(openSidebarPayload);
  };

  const getRows = () => {
    return accountBalanceData.map((item: any) => {
      return {
        key: transactionAccountId,
        cells: [
          {
            content: item?.createdAt ? (
              <FormattedDate date={new Date(item.createdAt).toISOString()} />
            ) : (
              <span aria-label="Not available" data-testid="not-available">
                {NOT_AVAILABLE}
              </span>
            ),
          },
          {
            content: item?.invoiceNumber ? (
              <Button
                onClick={() =>
                  handleSidebarAction({
                    sidebarType: GloballyAllowedSidebar.INVOICE_PREVIEW_V2,
                    payload: {
                      invoiceId: item?.invoiceNumber,
                    },
                    sidebarOptions: {
                      isOverlay: true,
                      sidebarWidth: '968px',
                    },
                  })
                }
                testId="invoice-link-btn"
                appearance="link"
                className="invoice-link-btn"
              >
                {item?.invoiceNumber}
              </Button>
            ) : (
              <span aria-label="Not available" data-testid="not-available">
                {NOT_AVAILABLE}
              </span>
            ),
          },
          {
            content: item?.type ? (
              <SimpleTD text={descriptiopMapping[item?.type] || capitalize(item?.type?.split('_').join(' '))} />
            ) : (
              <span aria-label="Not available" data-testid="not-available">
                {NOT_AVAILABLE}
              </span>
            ),
          },
          {
            content:
              item?.amount < 0 ? (
                <SimpleTD text={`USD ${(Math.abs(defaultTo(item?.amount, 0)) / 100).toFixed(2)}`} />
              ) : (
                <span aria-label="Not available" data-testid="not-available">
                  {NOT_AVAILABLE}
                </span>
              ),
          },
          {
            content:
              item?.amount > 0 ? (
                <SimpleTD text={`USD ${(Math.abs(defaultTo(item?.amount, 0)) / 100).toFixed(2)}`} />
              ) : (
                <span aria-label="Not available" data-testid="not-available">
                  {NOT_AVAILABLE}
                </span>
              ),
          },
          {
            content:
              item?.endingAmount !== undefined && item?.endingAmount !== null ? (
                <SimpleTD text={`USD ${(Math.abs(defaultTo(item?.endingAmount, 0)) / 100).toFixed(2)}`} />
              ) : (
                <span aria-label="Not available" data-testid="not-available">
                  {NOT_AVAILABLE}
                </span>
              ),
          },
        ],
      };
    });
  };

  const getHead = () => {
    const cells = [
      {
        content: 'Date',
        shouldTruncate: true,
        width: 10,
      },
      {
        content: 'Linked invoice',
        width: 10,
      },
      {
        content: 'Details',
        width: 10,
      },
      {
        content: 'Credit',
        width: 10,
      },
      {
        content: 'Debit',
        width: 10,
      },
      {
        content: 'Account balance',
        width: 10,
      },
    ];
    return {
      cells,
    };
  };

  return (
    <DynamicResultTable
      testId="account-balance-table"
      headingText={label}
      head={getHead()}
      rows={getRows()}
      emptyStateProps={emptyStateOrderProp}
      isLoading={isLoading}
    />
  );
};

export default TransactionAccountBalanceTable;
